import React, { useState, useEffect, useCallback } from "react";
import { Table, Tag, Card, Row, Col, Statistic, Input, Button, Space, message, Typography } from "antd";
import { SearchOutlined, EyeOutlined, ReloadOutlined, RobotOutlined, CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { API_CONST } from "../../../const";
import dayjs from "dayjs";

const { Title } = Typography;

const PageHeader = styled.div`
  margin-bottom: 24px;
  h1 { font-size: 24px; font-weight: 600; color: #014c75; margin: 0 0 8px 0; }
  p { color: #666; margin: 0; font-size: 14px; }
`;

const PageWrapper = styled.div`
  min-height: 100vh;

  .stats-row {
    margin-bottom: 24px;
  }

  .stats-card {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  .search-section {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
  }

  .table-card {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  .clickable-row {
    cursor: pointer;
  }

  .clickable-row:hover {
    background-color: #f5f5f5;
  }
`;

const MyAssessments = () => {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, completed: 0, processing: 0, failed: 0 });
  const [searchText, setSearchText] = useState("");

  const fetchAssessments = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API_CONST.GET_AI_PROJECT_ASSESSMENTS, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      if (data.success) {
        setAssessments(data.assessments || []);
      }
    } catch (error) {
      message.error("Failed to fetch assessments");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API_CONST.GET_AI_ASSESSMENT_STATS, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, []);

  useEffect(() => {
    fetchAssessments();
    fetchStats();
  }, [fetchAssessments, fetchStats]);

  const getStatusTag = (status) => {
    const statusConfig = {
      completed: { color: "success", icon: <CheckCircleOutlined />, text: "Completed" },
      processing: { color: "processing", icon: <ClockCircleOutlined />, text: "Processing" },
      failed: { color: "error", icon: <ExclamationCircleOutlined />, text: "Failed" },
      pending: { color: "default", icon: <ClockCircleOutlined />, text: "Pending" },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: "Assessment ID",
      dataIndex: "id",
      key: "id",
      width: 180,
      render: (id) => <span style={{ fontFamily: "monospace", fontSize: 12 }}>{id}</span>,
    },
    {
      title: "Description",
      dataIndex: "requirementDescription",
      key: "requirementDescription",
      ellipsis: true,
      render: (text) => text?.substring(0, 100) + (text?.length > 100 ? "..." : ""),
    },
    {
      title: "Technologies",
      dataIndex: "technologies",
      key: "technologies",
      width: 200,
      render: (techs) => (
        <Space wrap size={[4, 4]}>
          {(techs || []).slice(0, 3).map((tech, i) => (
            <Tag key={i} color="blue">{tech}</Tag>
          ))}
          {techs?.length > 3 && <Tag>+{techs.length - 3}</Tag>}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => getStatusTag(status),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date) => dayjs(date).format("MMM DD, YYYY"),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Button 
          type="link" 
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/customer/assessments/${record.id}`);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const filteredAssessments = assessments.filter((a) => {
    const searchLower = searchText.toLowerCase();
    return (
      a.id?.toLowerCase().includes(searchLower) ||
      a.requirementDescription?.toLowerCase().includes(searchLower) ||
      (a.technologies || []).some((t) => t.toLowerCase().includes(searchLower))
    );
  });

  return (
    <PageWrapper>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <PageHeader style={{ marginBottom: 0 }}>
          <h1>My Assessments</h1>
          <p>Track and review your AI project assessments</p>
        </PageHeader>
        <Button icon={<ReloadOutlined />} onClick={() => { fetchAssessments(); fetchStats(); }}>
          Refresh
        </Button>
      </div>

      <Row gutter={16} className="stats-row">
        <Col span={6}>
          <Card className="stats-card">
            <Statistic title="Total Assessments" value={stats.total} prefix={<RobotOutlined />} />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stats-card">
            <Statistic title="Completed" value={stats.completed} valueStyle={{ color: "#52c41a" }} prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stats-card">
            <Statistic title="Processing" value={stats.processing} valueStyle={{ color: "#1890ff" }} prefix={<ClockCircleOutlined />} />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stats-card">
            <Statistic title="Failed" value={stats.failed} valueStyle={{ color: "#ff4d4f" }} prefix={<ExclamationCircleOutlined />} />
          </Card>
        </Col>
      </Row>

      <Card className="table-card">
        <div className="search-section">
          <Input
            placeholder="Search by ID, description, or technology..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 400 }}
            allowClear
          />
        </div>
        <Table
          columns={columns}
          dataSource={filteredAssessments}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
          onRow={(record) => ({
            onClick: () => navigate(`/customer/assessments/${record.id}`),
            className: "clickable-row",
          })}
        />
      </Card>
    </PageWrapper>
  );
};

export default MyAssessments;
